import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Bot, CheckCircle, ExternalLink, FileText, PlayCircle } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getCertificateRequestsList,
  runCertificateRequestAutomation,
  updateCertificateRequestStatus,
  type CertificateRequestListItem,
  type CertificateRequestStatus,
} from "@/services/certificateRequestsService";
import {
  getIdDigitalCertificateUrl,
  type CertificateRequestCompanyType,
} from "@/pages/certificate-requests/idDigitalUrls";

export const CERTIFICATE_REQUESTS_QUERY_KEY = [
  "platform",
  "certificate-requests",
] as const;

const STATUS_BADGE_VARIANT: Record<CertificateRequestStatus, StatusBadgeVariant> = {
  pending: "warning",
  in_progress: "info",
  fulfilled: "success",
  failed: "danger",
};

function fullName(row: CertificateRequestListItem): string {
  const parts = [
    row.firstName,
    row.secondName?.trim(),
    row.firstLastName,
    row.secondLastName?.trim(),
  ].filter(Boolean);
  return parts.join(" ");
}

export function CertificateRequestsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();
  const queryClient = useQueryClient();

  const statusParam = searchParams.get("status") ?? "all";
  const statusFilter: CertificateRequestStatus | undefined =
    statusParam === "pending" ||
    statusParam === "in_progress" ||
    statusParam === "fulfilled" ||
    statusParam === "failed"
      ? statusParam
      : undefined;

  const setStatusFilter = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "all") next.delete("status");
          else next.set("status", value);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
    }),
    [pagination.page, pagination.limit, statusFilter]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [...CERTIFICATE_REQUESTS_QUERY_KEY, listParams],
    queryFn: () =>
      getCertificateRequestsList(
        listParams.page,
        listParams.limit,
        listParams.status
      ),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: CertificateRequestStatus;
    }) => updateCertificateRequestStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATE_REQUESTS_QUERY_KEY,
      });
    },
    onError: () => {
      toast.error(t("certificateRequests.page.statusUpdateError"));
    },
  });

  const runAutomationMutation = useMutation({
    mutationFn: (id: string) => runCertificateRequestAutomation(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATE_REQUESTS_QUERY_KEY,
      });
      toast.success(t("certificateRequests.page.automationSuccess"));
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      const message =
        err.response?.data?.message ??
        err.message ??
        t("certificateRequests.page.automationFailed");
      toast.error(message);
    },
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("certificateRequests.page.error"));
    }
  }, [isError, t]);

  const items: CertificateRequestListItem[] = data?.data ?? [];
  const loading = isLoading;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("certificateRequests.page.title")}
        description={t("certificateRequests.page.description")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusParam} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t("certificateRequests.page.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("certificateRequests.page.filterAll")}
            </SelectItem>
            <SelectItem value="pending">
              {t("certificateRequests.page.statusPending")}
            </SelectItem>
            <SelectItem value="in_progress">
              {t("certificateRequests.page.statusInProgress")}
            </SelectItem>
            <SelectItem value="fulfilled">
              {t("certificateRequests.page.statusFulfilled")}
            </SelectItem>
            <SelectItem value="failed">
              {t("certificateRequests.page.statusFailed")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("certificateRequests.page.emptyTitle")}
            message={t("certificateRequests.page.emptyMessage")}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("certificateRequests.page.tenant")}</TableHead>
                  <TableHead>{t("certificateRequests.page.applicant")}</TableHead>
                  <TableHead>{t("certificateRequests.page.companyType")}</TableHead>
                  <TableHead>{t("certificateRequests.page.date")}</TableHead>
                  <TableHead>{t("certificateRequests.page.status")}</TableHead>
                  <TableHead className="w-[60px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const idDigitalUrl = getIdDigitalCertificateUrl(
                    row.companyType as CertificateRequestCompanyType,
                    row.durationYears
                  );
                  const actions: TableAction[] = [
                    {
                      label: t("certificateRequests.page.viewDetails"),
                      icon: FileText,
                      onClick: () => navigate(`/certificate-requests/${row.id}`),
                    },
                  ];
                  if (
                    row.status === "pending" ||
                    row.status === "in_progress"
                  ) {
                    actions.push({
                      label: t("certificateRequests.page.fillFormAutomatically"),
                      icon: Bot,
                      onClick: () =>
                        runAutomationMutation.mutate(row.id),
                    });
                  }
                  actions.push({
                    label: t("certificateRequests.page.openIdDigital"),
                    icon: ExternalLink,
                    onClick: () => window.open(idDigitalUrl, "_blank", "noopener"),
                  });
                  if (row.status === "pending") {
                    actions.push({
                      label: t("certificateRequests.page.markInProgress"),
                      icon: PlayCircle,
                      onClick: () =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: "in_progress",
                        }),
                    });
                  }
                  if (
                    row.status === "pending" ||
                    row.status === "in_progress"
                  ) {
                    actions.push({
                      label: t("certificateRequests.page.markFulfilled"),
                      icon: CheckCircle,
                      onClick: () =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: "fulfilled",
                        }),
                    });
                  }
                  return (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer transition-colors duration-150 hover:bg-muted/50"
                      onClick={() => navigate(`/certificate-requests/${row.id}`)}
                    >
                      <TableCell className="font-medium">
                        <span className="block">{row.tenantName || "—"}</span>
                        {row.tenantRut && (
                          <span className="block text-xs text-muted-foreground">
                            RUT {row.tenantRut}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {fullName(row)}
                      </TableCell>
                      <TableCell className="uppercase">
                        {row.companyType}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDisplayDate(row.createdAt, {
                          includeTime: true,
                          locale: i18n.language,
                        })}
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={STATUS_BADGE_VARIANT[row.status]}>
                          {row.status === "pending"
                            ? t("certificateRequests.page.statusPending")
                            : row.status === "in_progress"
                              ? t("certificateRequests.page.statusInProgress")
                              : row.status === "fulfilled"
                                ? t("certificateRequests.page.statusFulfilled")
                                : t("certificateRequests.page.statusFailed")}
                        </StatusBadge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <TableActionsMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("certificateRequests.page.rowsPerPage")}
              pageOfLabel={t("certificateRequests.page.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
